import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DiyLayout from "../../components/Layout/DiyLayout";
import DiyDisputeDetailModal, { type DiyDisputeCard } from "../../components/diy/DiyDisputeDetailModal";
import { useDiySelfClient } from "../../hooks/useDiySelfClient";
import { disputesApi, type CreateDisputeRequest } from "../../api/disputesApi";
import { disputeLettersApi } from "../../api/disputeLettersApi";
import type { DisputeItem, LetterTemplate } from "../../types";
import { normalizeDiyDisputeBucket, getDisputeBureaus } from "./diyShared";
import { TableSkeletonRows } from "../../components/ui/Skeleton";
import {
  DISPUTE_ACCOUNT_TYPES,
  DISPUTE_STATUSES,
  FALLBACK_LETTER_TEMPLATES,
  modalInputClass,
  modalCardClass,
} from "../../components/diy/diyModalConstants";
import { ScrollReveal } from "../../components/diy/ScrollReveal";
import "../../styles/diy-animations.css";

function daysSince(iso: string): number {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) {
    return 0;
  }
  return Math.max(0, Math.floor((Date.now() - t) / 86400000));
}

type FilterTab = "All" | "Pending" | "In Progress" | "Resolved" | "Denied";

function disputeFilterTab(d: DisputeItem): FilterTab {
  const s = d.status.toLowerCase();
  if (s.includes("denied")) {
    return "Denied";
  }
  const b = normalizeDiyDisputeBucket(d.status);
  if (b === "resolved") {
    return "Resolved";
  }
  if (b === "awaiting") {
    return "Pending";
  }
  return "In Progress";
}

function leftBorderForDispute(d: DisputeItem): string {
  const t = disputeFilterTab(d);
  if (t === "Pending") {
    return "#F59E0B";
  }
  if (t === "In Progress") {
    return "#0EA5E9";
  }
  if (t === "Resolved") {
    return "#10B981";
  }
  if (t === "Denied") {
    return "#EF4444";
  }
  return "#0EA5E9";
}

const FILTER_TABS: FilterTab[] = ["All", "Pending", "In Progress", "Resolved", "Denied"];

const DiyMyDisputesPage: React.FC = () => {
  const { client, clientId, loading: clientLoading, error: clientError, reload: reloadClient } = useDiySelfClient();
  const [disputes, setDisputes] = useState<DisputeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<DiyDisputeCard | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [filter, setFilter] = useState<FilterTab>("All");
  const [letterTemplates, setLetterTemplates] = useState<LetterTemplate[]>([]);
  const [dCreditor, setDCreditor] = useState("");
  const [dAccountType, setDAccountType] = useState<string>(DISPUTE_ACCOUNT_TYPES[0]);
  const [dAccountNumber, setDAccountNumber] = useState("");
  const [dBalance, setDBalance] = useState("");
  const [dStatus, setDStatus] = useState<string>(DISPUTE_STATUSES[0]);
  const [dDisputeReason, setDDisputeReason] = useState("");
  const [dEquifax, setDEquifax] = useState(false);
  const [dExperian, setDExperian] = useState(false);
  const [dTransUnion, setDTransUnion] = useState(false);
  const [dLetterType, setDLetterType] = useState("");
  const [dNotes, setDNotes] = useState("");
  const [disputeSaving, setDisputeSaving] = useState(false);
  const [disputeFormError, setDisputeFormError] = useState("");

  const clientName = client ? `${client.firstName} ${client.lastName}`.trim() : "";

  const disputeLetterOptions = useMemo(
    () => (letterTemplates.length > 0 ? letterTemplates : FALLBACK_LETTER_TEMPLATES),
    [letterTemplates]
  );

  const loadDisputes = useCallback(async () => {
    if (!clientId) {
      setDisputes([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const list = await disputesApi.getAllForClient(clientId);
      setDisputes(list);
    } catch {
      setError("Could not load disputes.");
      setDisputes([]);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    void loadDisputes();
  }, [loadDisputes]);

  useEffect(() => {
    if (!addOpen) {
      return;
    }
    void disputeLettersApi.getTemplates().then(setLetterTemplates).catch(() => setLetterTemplates([]));
  }, [addOpen]);

  useEffect(() => {
    if (!addOpen) {
      return;
    }
    setDLetterType((prev) => prev || disputeLetterOptions[0]?.typeKey || "");
  }, [addOpen, disputeLetterOptions]);

  const filtered = useMemo(() => {
    if (filter === "All") {
      return disputes;
    }
    return disputes.filter((d) => disputeFilterTab(d) === filter);
  }, [disputes, filter]);

  const progressPct =
    disputes.length === 0
      ? 0
      : Math.round((disputes.filter((d) => normalizeDiyDisputeBucket(d.status) === "resolved").length / disputes.length) * 100);

  const openAdd = () => {
    setDCreditor("");
    setDAccountType(DISPUTE_ACCOUNT_TYPES[0]);
    setDAccountNumber("");
    setDBalance("");
    setDStatus(DISPUTE_STATUSES[0]);
    setDDisputeReason("");
    setDEquifax(false);
    setDExperian(false);
    setDTransUnion(false);
    setDLetterType(disputeLetterOptions[0]?.typeKey ?? "");
    setDNotes("");
    setDisputeFormError("");
    setAddOpen(true);
  };

  const submitNewDispute = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientId) {
      return;
    }
    setDisputeSaving(true);
    setDisputeFormError("");
    const balanceNum = Number.parseFloat(dBalance);
    if (dBalance.trim() === "" || Number.isNaN(balanceNum)) {
      setDisputeFormError("Enter a valid balance.");
      setDisputeSaving(false);
      return;
    }
    const now = new Date().toISOString();
    const payload: CreateDisputeRequest = {
      creditor: dCreditor.trim(),
      accountType: dAccountType,
      accountNumber: dAccountNumber.trim(),
      balance: balanceNum,
      status: dStatus,
      disputeReason: dDisputeReason.trim(),
      disputeStrategy: "",
      letterType: dLetterType.trim(),
      priorityRank: 0,
      estimatedScoreImpact: "",
      equifaxDisputed: dEquifax,
      experianDisputed: dExperian,
      transUnionDisputed: dTransUnion,
      roundNumber: 1,
      statuteOfLimitationsFlag: false,
      notes: dNotes.trim(),
      lastLetterSentAt: null,
      responseReceivedAt: null,
      createdAt: now,
      updatedAt: now,
    };
    try {
      await disputesApi.create(clientId, payload);
      await loadDisputes();
      setAddOpen(false);
    } catch {
      setDisputeFormError("Could not create dispute.");
    } finally {
      setDisputeSaving(false);
    }
  };

  const toCard = (d: DisputeItem): DiyDisputeCard => ({ ...d, clientName });

  const badgeFor = (d: DisputeItem) => {
    const t = disputeFilterTab(d);
    const map: Record<FilterTab, { bg: string; color: string }> = {
      All: { bg: "rgba(14,165,233,0.1)", color: "#0EA5E9" },
      Pending: { bg: "rgba(245,158,11,0.1)", color: "#F59E0B" },
      "In Progress": { bg: "rgba(14,165,233,0.1)", color: "#0EA5E9" },
      Resolved: { bg: "rgba(16,185,129,0.1)", color: "#10B981" },
      Denied: { bg: "rgba(239,68,68,0.1)", color: "#EF4444" },
    };
    const st = map[t];
    return { label: t === "In Progress" ? "Active" : t, ...st };
  };

  const glass = {
    background: "rgba(255, 255, 255, 0.03)",
    border: "1px solid rgba(255, 255, 255, 0.07)",
  };

  return (
    <DiyLayout>
      <div className="mx-auto max-w-lg md:max-w-2xl">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <header>
            <h1 style={{ fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: 22, color: "#F1F5F9" }}>My Disputes</h1>
            <p className="mt-1" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#94A3B8" }}>
              Track your dispute progress
            </p>
          </header>
          <button
            type="button"
            onClick={openAdd}
            disabled={!clientId}
            className="cursor-pointer rounded-xl border-0 px-4 py-2 text-sm font-semibold text-white transition-transform hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-40"
            style={{
              fontFamily: "'DM Sans', sans-serif",
              background: "linear-gradient(135deg, #10B981, #0EA5E9)",
              boxShadow: "0 4px 15px rgba(16,185,129,0.3)",
            }}
          >
            Add Dispute
          </button>
        </div>

        {clientError ? (
          <p className="text-sm" style={{ color: "#FECACA", fontFamily: "'DM Sans', sans-serif" }}>
            {clientError}{" "}
            <button type="button" className="underline" style={{ color: "#0EA5E9" }} onClick={() => void reloadClient()}>
              Retry
            </button>
          </p>
        ) : null}

        {disputes.length > 0 && (
          <div className="mb-6 rounded-xl p-4" style={glass}>
            <div className="mb-2 flex justify-between text-xs" style={{ fontFamily: "'DM Sans', sans-serif", color: "#94A3B8" }}>
              <span>Progress</span>
              <span>
                {disputes.filter((d) => normalizeDiyDisputeBucket(d.status) === "resolved").length} / {disputes.length}{" "}
                resolved
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full" style={{ background: "rgba(255,255,255,0.06)" }}>
              <div
                className="h-2 rounded-full"
                style={{
                  width: `${progressPct}%`,
                  background: "linear-gradient(90deg, #10B981, #0EA5E9)",
                  transition: "width 0.3s ease",
                }}
              />
            </div>
          </div>
        )}

        <ScrollReveal>
          <div className="-mx-1 mb-6 flex gap-2 overflow-x-auto pb-1">
            {FILTER_TABS.map((tab) => {
              const active = filter === tab;
              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setFilter(tab)}
                  className="shrink-0 cursor-pointer rounded-xl border-0 px-4 py-2 text-sm transition-all duration-200"
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 600,
                    ...(active
                      ? {
                          background: "linear-gradient(135deg, #10B981, #0EA5E9)",
                          color: "white",
                          boxShadow: "0 4px 12px rgba(16,185,129,0.25)",
                        }
                      : {
                          background: "rgba(255,255,255,0.04)",
                          color: "#94A3B8",
                        }),
                  }}
                >
                  {tab}
                </button>
              );
            })}
          </div>
        </ScrollReveal>

        {loading || clientLoading ? (
          <TableSkeletonRows rows={6} cols={1} />
        ) : error ? (
          <p className="text-sm" style={{ color: "#FECACA" }}>
            {error}{" "}
            <button type="button" className="underline" style={{ color: "#0EA5E9" }} onClick={() => void loadDisputes()}>
              Retry
            </button>
          </p>
        ) : disputes.length === 0 ? (
          <ScrollReveal>
            <div className="rounded-2xl px-6 py-12 text-center" style={glass}>
              <h2 style={{ fontFamily: "'Sora', sans-serif", fontWeight: 600, fontSize: 18, color: "#F1F5F9" }}>No disputes yet</h2>
              <p className="mt-2" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#94A3B8" }}>
                Once AI analyzes your report, you can select items to dispute.
              </p>
              <Link
                to="/diy/my-report"
                className="mt-6 inline-block rounded-xl px-6 py-3 text-center font-semibold text-white no-underline transition-transform hover:scale-[1.02]"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  background: "linear-gradient(135deg, #10B981, #0EA5E9)",
                  boxShadow: "0 4px 15px rgba(16,185,129,0.3)",
                }}
              >
                Go to credit report
              </Link>
            </div>
          </ScrollReveal>
        ) : filtered.length === 0 ? (
          <p style={{ fontFamily: "'DM Sans', sans-serif", color: "#64748B", fontSize: 14 }}>No disputes in this filter.</p>
        ) : (
          filtered.map((d, i) => {
            const b = badgeFor(d);
            const border = leftBorderForDispute(d);
            const bureaus = getDisputeBureaus(d);
            return (
              <ScrollReveal key={d.id} delay={i * 0.05}>
                <button
                  type="button"
                  onClick={() => setSelected(toCard(d))}
                  className="card-hover mb-3 w-full cursor-pointer rounded-xl p-4 text-left transition-all duration-300"
                  style={{
                    ...glass,
                    borderLeft: `3px solid ${border}`,
                  }}
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <p style={{ fontFamily: "'Sora', sans-serif", fontWeight: 600, fontSize: 14, color: "#F1F5F9" }}>{d.creditor}</p>
                    <span
                      className="rounded-full px-3 py-1"
                      style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontWeight: 600,
                        fontSize: 10,
                        background: b.bg,
                        color: b.color,
                      }}
                    >
                      {b.label}
                    </span>
                  </div>
                  <p className="mt-2 text-xs" style={{ fontFamily: "'DM Sans', sans-serif", color: "#94A3B8" }}>
                    {d.accountType} · ${d.balance?.toLocaleString?.() ?? d.balance} · {d.status}
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {bureaus.map((bu) => (
                      <span
                        key={bu}
                        className="rounded-full px-2 py-0.5 text-[10px] font-medium"
                        style={{ background: "rgba(255,255,255,0.06)", color: "#CBD5E1", fontFamily: "'DM Sans', sans-serif" }}
                      >
                        {bu}
                      </span>
                    ))}
                    <span className="text-xs" style={{ fontFamily: "'DM Sans', sans-serif", color: "#64748B" }}>
                      Filed {new Date(d.createdAt).toLocaleDateString()} · Day {daysSince(d.createdAt)}
                    </span>
                  </div>
                </button>
              </ScrollReveal>
            );
          })
        )}
      </div>

      {addOpen ? (
        <div
          className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70 p-4 sm:items-center"
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.target === e.currentTarget && setAddOpen(false)}
        >
          <div className={`${modalCardClass} max-h-[90vh] w-full max-w-lg overflow-y-auto`}>
            <h3 className="text-lg font-semibold text-white">Add dispute</h3>
            <form className="mt-4 space-y-3" onSubmit={(e) => void submitNewDispute(e)}>
              <label className="block text-xs text-slate-500">
                Creditor
                <input
                  className={modalInputClass}
                  value={dCreditor}
                  onChange={(e) => setDCreditor(e.target.value)}
                  required
                />
              </label>
              <label className="block text-xs text-slate-500">
                Account type
                <select className={modalInputClass} value={dAccountType} onChange={(e) => setDAccountType(e.target.value)}>
                  {DISPUTE_ACCOUNT_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-xs text-slate-500">
                Account number
                <input className={modalInputClass} value={dAccountNumber} onChange={(e) => setDAccountNumber(e.target.value)} />
              </label>
              <label className="block text-xs text-slate-500">
                Balance
                <input
                  className={modalInputClass}
                  type="number"
                  step="0.01"
                  value={dBalance}
                  onChange={(e) => setDBalance(e.target.value)}
                  required
                />
              </label>
              <label className="block text-xs text-slate-500">
                Status
                <select className={modalInputClass} value={dStatus} onChange={(e) => setDStatus(e.target.value)}>
                  {DISPUTE_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-xs text-slate-500">
                Dispute reason
                <textarea
                  className={`${modalInputClass} min-h-[80px]`}
                  value={dDisputeReason}
                  onChange={(e) => setDDisputeReason(e.target.value)}
                  required
                />
              </label>
              <fieldset className="rounded-lg border border-slate-800 p-3">
                <legend className="px-1 text-xs text-slate-500">Bureaus</legend>
                <div className="mt-2 flex flex-wrap gap-4 text-sm">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={dEquifax} onChange={(e) => setDEquifax(e.target.checked)} />
                    Equifax
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={dExperian} onChange={(e) => setDExperian(e.target.checked)} />
                    Experian
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={dTransUnion} onChange={(e) => setDTransUnion(e.target.checked)} />
                    TransUnion
                  </label>
                </div>
              </fieldset>
              <label className="block text-xs text-slate-500">
                Letter type
                <select className={modalInputClass} value={dLetterType} onChange={(e) => setDLetterType(e.target.value)} required>
                  {disputeLetterOptions.map((t) => (
                    <option key={t.typeKey} value={t.typeKey}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-xs text-slate-500">
                Notes
                <textarea className={modalInputClass} value={dNotes} onChange={(e) => setDNotes(e.target.value)} />
              </label>
              {disputeFormError ? <p className="text-sm text-red-300">{disputeFormError}</p> : null}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  className="rounded-lg border border-slate-600 px-4 py-2 text-sm text-slate-300"
                  onClick={() => setAddOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={disputeSaving}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-40"
                >
                  {disputeSaving ? "Saving…" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      <DiyDisputeDetailModal
        card={selected}
        onClose={() => setSelected(null)}
        onSaved={async () => {
          await loadDisputes();
        }}
      />
    </DiyLayout>
  );
};

export default DiyMyDisputesPage;
